import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import cn from 'classnames';
import dynamic from 'next/dynamic';
import { useViewport } from '@sbt-web/hooks';
import { FullModal } from '@sbt-web/ui';
import { isViewableRating } from '@/client/components/Detail/UserRating';
import useUser from '@/client/contexts/UserContext';
import { previewReviewClickAction } from '@/tools/tracking/ProfileTracking';
import type { UserFeedback } from '@/types';
import { isProfileOwner } from '@/utils/profile';
import { TextualReview } from '../TextualReview';
import { TextualReviewModalContent } from '../TextualReviewModalContent';
import styles from './index.module.scss';
interface TextualReviewBoxProps {
  userName: string;
  ownerId: string;
  onPreviewClick: () => void;
  onModalClose: () => void;
  modalOpen: boolean;
  score?: number;
  reviewCount?: number;
  feedbacks?: UserFeedback[];
}
type ModalProps = Omit<TextualReviewBoxProps, 'feedbacks' | 'onPreviewClick'> & {
  isMobile: boolean;
};
const DynamicTextualReviewModal = dynamic(() => import('../TextualReviewModal').then(({
  TextualReviewModal
}) => TextualReviewModal), {
  ssr: false
});
const Modal = t0 => {
  const $ = _c(10);
  const {
    isMobile,
    ownerId,
    userName,
    score,
    reviewCount,
    modalOpen,
    onModalClose
  } = t0;
  const t1 = score ?? 0;
  const t2 = reviewCount ?? 0;
  let t3;
  if ($[0] !== ownerId || $[1] !== t1 || $[2] !== t2 || $[3] !== userName) {
    t3 = <TextualReviewModalContent userName={userName} score={t1} reviewCount={t2} ownerId={ownerId} />;
    $[0] = ownerId;
    $[1] = t1;
    $[2] = t2;
    $[3] = userName;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const TextualReviewContentComponent = t3;
  let t4;
  if ($[5] !== TextualReviewContentComponent || $[6] !== isMobile || $[7] !== modalOpen || $[8] !== onModalClose) {
    t4 = isMobile ? <FullModal title="Recensioni" open={modalOpen} onClose={onModalClose} inset={true}>{TextualReviewContentComponent}</FullModal> : <DynamicTextualReviewModal onClose={onModalClose} open={modalOpen}>{TextualReviewContentComponent}</DynamicTextualReviewModal>;
    $[5] = TextualReviewContentComponent;
    $[6] = isMobile;
    $[7] = modalOpen;
    $[8] = onModalClose;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
const NoReviewBox = t0 => {
  const $ = _c(6);
  const {
    userName
  } = t0;
  const t1 = `${userName} non ha recensioni (per ora)`;
  let t2;
  if ($[0] !== t1) {
    t2 = <p className="subheading">{t1}</p>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("body-text small", [styles.empty_review_text]);
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className={t3}>Concludi un affare con questo utente in modo sicuro grazie alla nostra{" "}<a target="_blank" rel="noreferrer" href="https://assistenza.subito.it/hc/it/articles/4403733868945-Protezione-Acquisti?utm_medium=autopromo&utm_source=subito&utm_campaign=free-attraction-pmm_education_user_profile_buyer&utm_content=autopromo_pmm_widget_tuttosubito_user_profile_page_mar2025" className={styles["protection-link"]}>protezione acquisti</a>.</p>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t2) {
    t5 = <>{t2}{t4}</>;
    $[4] = t2;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
};
export const TextualReviewBox = t0 => {
  const $ = _c(17);
  const {
    userName,
    score,
    reviewCount,
    ownerId,
    feedbacks,
    onPreviewClick,
    onModalClose,
    modalOpen
  } = t0;
  const {
    isMobile
  } = useViewport();
  const {
    user
  } = useUser();
  const lastTextualReview = feedbacks && feedbacks.length > 0 && isViewableRating(reviewCount, score) ? feedbacks[0] : undefined;
  const [isOwner, setIsOwner] = useState(false);
  let t1;
  if ($[0] !== ownerId || $[1] !== user?.id) {
    t1 = () => {
      setIsOwner(isProfileOwner(ownerId, user?.id));
    };
    $[0] = ownerId;
    $[1] = user?.id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = user?.id;
  let t3;
  if ($[3] !== ownerId || $[4] !== t2) {
    t3 = [ownerId, t2];
    $[3] = ownerId;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(t1, t3);
  let t4;
  if ($[6] !== isMobile || $[7] !== isOwner || $[8] !== lastTextualReview || $[9] !== modalOpen || $[10] !== onModalClose || $[11] !== onPreviewClick || $[12] !== ownerId || $[13] !== reviewCount || $[14] !== score || $[15] !== userName) {
    t4 = <div>{lastTextualReview ? <><h6 className={cn(styles.title, "headline-6")}>Dicono di {isOwner ? "te" : userName}</h6><TextualReview isPreview={true} userId={ownerId} score={lastTextualReview.feedback.score} reviewCount={reviewCount} onClick={() => {
          previewReviewClickAction(ownerId);
          onPreviewClick();
        }} text={lastTextualReview.feedback.textReview ?? undefined} isAutomaticReview={lastTextualReview.feedback.isAutomaticReview ?? undefined} classes={[styles.review]} /><Modal isMobile={isMobile} onModalClose={onModalClose} score={score} reviewCount={reviewCount} modalOpen={modalOpen} ownerId={ownerId} userName={userName} /></> : <NoReviewBox userName={userName} />}</div>;
    $[6] = isMobile;
    $[7] = isOwner;
    $[8] = lastTextualReview;
    $[9] = modalOpen;
    $[10] = onModalClose;
    $[11] = onPreviewClick;
    $[12] = ownerId;
    $[13] = reviewCount;
    $[14] = score;
    $[15] = userName;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
};